// Design-release rebinding apply service — instances (Plan B3 Task 4,
// cogo.template.v1 content-service loop, spec §3.4/§6.5's SECOND variation
// axis alongside B2's content-slot loop).
//
// PRESENTATION-only: rebinding replaces the project's design tokens
// (`DesignSystemPreset.tokens`) and nothing else — it never reads or writes
// any block's `ui.json`/`flow`/`data` (structure/behavior). That invariance
// is asserted at the test level (`__tests__/design-rebind.test.tsx`) by
// checking `useEditorStore.getState().blocks` keeps referential equality
// across an apply call — this module never touches `state.blocks`.
//
// Fail-closed guard, reused verbatim: `entry.pkg.designBinding.rebinding`
// gates this the exact same way `@cogo/template`'s `instantiate.ts` gates a
// compile-time design-system swap — `'design-rebinding-not-allowed'` is that
// module's own closed TypedFailure code (see its `designSystem !== undefined`
// branch), reused here (not re-invented) so the compile-time (instantiate)
// and edit-time (this) rebind paths speak the same failure vocabulary. A
// pinned package must refuse this call outright, regardless of project
// state — checked BEFORE the "is a project even open" guard below.

import type { TemplateRegistryEntry } from '../registry'
import type { DesignSystemPreset } from '@/features/design-system/lib/fetch-presets'
import type { DesignTokens } from '@/features/page-editor/store/types'
import { useStudioStore } from '@/store/studio-store'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { withEditorAutoSaveSuppressed } from '@/store/auto-save-bridge'
import * as api from '@/services/project-api'
import { readInstantiatedFrom } from './project-lineage'

export interface ApplyDesignRebindResult {
  ok: boolean
  error?: string
}

/**
 * Persists `preset.tokens` as the current project's design tokens (the same
 * `saveProjectMeta(projectId, {tokens})` path `save-preset.ts`'s own
 * `design_system_presets.tokens` column round-trips through — see
 * `fetch-presets.ts`'s `DesignSystemPreset.tokens: DesignSystemState`),
 * mirrors the result into both live stores (suppressed like
 * `apply-slot-values.ts`'s `reflectAppliedChanges` — see below), and records
 * the rebind additively onto the project's template lineage via
 * `persistProjectDesignRelease`.
 *
 * Fail-closed at the persist stage: a `saveProjectMeta` rejection returns
 * `{ok:false, error}` WITHOUT touching either store — an honest "nothing
 * changed" result, same posture as `apply-slot-values.ts`'s validate-first
 * stages. Once that real write has landed, the lineage record is
 * additive/best-effort (mirrors `markTemplateDivergenceOnForeignEdit`'s
 * fire-and-forget swallow): a lineage-recording failure must never roll back
 * — or even surface as a failure of — a tokens apply that has ALREADY landed
 * server-side.
 */
export async function applyDesignRebind(
  entry: TemplateRegistryEntry,
  preset: DesignSystemPreset,
): Promise<ApplyDesignRebindResult> {
  if (entry.pkg.designBinding.rebinding !== 'allowed') {
    return { ok: false, error: 'design-rebinding-not-allowed' }
  }

  const studio = useStudioStore.getState()
  const projectId = studio.currentProjectId
  const editor = useEditorStore.getState()
  if (!projectId || !editor.app) {
    return { ok: false, error: 'design-rebind-no-project' }
  }

  try {
    await api.saveProjectMeta(projectId, { tokens: preset.tokens })
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) }
  }

  // Reflect ONLY after the real persist above landed — the canvas
  // (`useEditorStore`) first, then the studio-store project mirror, same
  // ordering `apply-slot-values.ts`'s `reflectAppliedChanges` uses.
  //
  // `withEditorAutoSaveSuppressed` wraps the `useEditorStore.setState` call
  // for the same reason `reflectAppliedChanges` does (see its doc comment):
  // this mutation is purely a display mirror of a value ALREADY persisted
  // above. `auto-save-bridge.ts`'s EditorStore subscription doesn't
  // currently key its change-detection snapshot on `tokens` at all (only
  // `app`+`blocks` — see `buildEditorSnapshot`), so this specific call isn't
  // load-bearing against TODAY's dedupe logic, but the suppression is kept
  // for the same defense-in-depth reason every other store-mirror write in
  // this content-service loop uses it: a reflection of already-persisted
  // content must never be mistaken for a fresh, unpersisted foreign edit.
  withEditorAutoSaveSuppressed(() => {
    useEditorStore.setState((state) => {
      // `DesignSystemPreset.tokens` (a `DesignSystemState` style/palette
      // recipe — see fetch-presets.ts) round-trips through the SAME
      // `tokens` column `save-preset.ts` writes it to verbatim; `DesignTokens`
      // (this store's field type) is a deliberately loose, open-indexed
      // shape (`[key: string]: unknown`) precisely so it can carry either
      // representation without a lossy conversion.
      state.tokens = preset.tokens as unknown as DesignTokens
    })
  })
  useStudioStore.setState((s) => {
    const project = s.currentProject as unknown as { tokens?: unknown } | null
    if (!project) return
    project.tokens = preset.tokens
  })

  const metadata = (studio.projectMeta?.metadata as Record<string, unknown> | undefined) ?? null
  if (readInstantiatedFrom(metadata)) {
    try {
      await api.persistProjectDesignRelease(projectId, {
        presetId: preset.id,
        appliedAt: new Date().toISOString(),
      })
    } catch (err) {
      console.warn('[apply-design-rebind] Failed to persist designRelease lineage:', err)
    }
  }

  return { ok: true }
}
